import React, { FunctionComponent, useState } from "react";
import { connect } from "react-redux";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActiveRounded";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNoneRounded";
import {
  Badge,
  Popper,
  IconButton,
  Paper,
  ClickAwayListener,
  MenuList,
  Divider,
  Box,
  MenuItem,
  Button,
  Typography,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { red } from "@mui/material/colors";
import { isEqual, startCase, uniq } from "lodash";
import { getReminders, markAllReminderRead } from "../../actions/reminders";
import NotificationItem from "./NotificationItem";
import { serviceProviderActions } from "../../actions";
import { getClientDetails } from "../../api/client";
import { isReadOnlyUser } from "./SideBar";
import useMobileScreen from "../../hooks/useMobileScreen";
import { Client } from "../../interfaces/ClientInterface";
import { ServiceProvider } from "../../interfaces/ServiceProvidersInterface";
import { useAppDispatch } from "../../store/hooks";
import { ClientLinkRequests } from "./ClientLinkRequests";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";
import { getClientLinkRequests } from "../../api/clientLink";
import { ClientLinkRequest } from "../../interfaces/ClientLinkRequests";

// @ts-expect-error -css
import styles from "./styles.module.css";

export const StyledBadge = styled(Badge)({
  "& .MuiBadge-badge": {
    right: -3,
    top: 5,
    padding: "0 2px",
    background: red[500],
    color: "white"
  }
});

interface NotificationsInterface {
  notifications: Array<Record<string, any>>;
  getRemindersAction: () => void;
  resourceCentreId: string | number;
  serviceProviders: Array<ServiceProvider>;
  loadServiceProviders: (id: number) => void;
  permissionGroup: string;
}

export enum RemindersTypes {
  RemindCustomerOnFollowup = "RemindCustomerOnFollowup",
  CreateBookingForFollowup = "CreateBookingForFollowup",
  PatientBookedFromPublicBooking = "PatientBookedFromPublicBooking",
  LabRecordReady = "LabRecordReady",
  SubscriptionInvoiceCreated = "SubscriptionInvoiceCreated",
  SubscriptionInvoiceDocumentUploaded = "SubscriptionInvoiceDocumentUploaded",
  NotificationToResourceCentre = "NotificationToResourceCentre",
  ExpiringStocks = "ExpiringStocks",
  FinalizedAssessmentLabs = "FinalizedAssessmentLabs"
}

const Notifications: FunctionComponent<NotificationsInterface> = ({
  notifications,
  getRemindersAction,
  resourceCentreId,
  serviceProviders,
  loadServiceProviders,
  permissionGroup
}) => {
  const dispatch = useAppDispatch();
  const rc = useCurrentResourceCentre();
  const isMobileScreen = useMobileScreen(960);

  const [clients, setClients] = React.useState<Array<Client>>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isGrouped, setIsGrouped] = React.useState<boolean>(false);
  const [isSortedByUnread, setIsSortedByUnread] = useState<boolean>(false);
  const [sentClientLinkRequests, setSentClientLinkRequests] = useState<ClientLinkRequest[]>([]);
  const [receivedClientLinkRequests, setReceivedClientLinkRequests] = useState<ClientLinkRequest[]>(
    []
  );

  const [openNotification, setOpenNotification] = React.useState<HTMLElement | null>(null);

  let iconColor = "inherit";
  if (isMobileScreen) {
    iconColor = "fff";
  } else if (openNotification) {
    iconColor = "00A358";
  }

  const notificationsRef = React.useRef(notifications);
  if (!isEqual(notificationsRef.current, notifications)) {
    notificationsRef.current = notifications;
  }

  const handleClickNotification = (event) => {
    if (openNotification && openNotification?.contains(event.target)) {
      setOpenNotification(null);
    } else {
      setOpenNotification(event.currentTarget);
    }
  };

  const handleCloseNotification = () => {
    setOpenNotification(null);
  };

  const validNotifications = React.useMemo(() => {
    const filteredNotifications = notifications.filter((notification) => {
      if (notification.type === RemindersTypes.LabRecordReady) return true;
      if (notification.type === RemindersTypes.SubscriptionInvoiceCreated) return true;
      if (notification.type === RemindersTypes.SubscriptionInvoiceDocumentUploaded) return true;
      if (notification.type === RemindersTypes.ExpiringStocks) return true;
      return (
        clients.some((client) => client.id === Number(notification.data.clientId)) &&
        serviceProviders.some(
          (serviceProvider) => serviceProvider.id === Number(notification.data.serviceProviderId)
        )
      );
    });
    if (isSortedByUnread) {
      filteredNotifications.sort((a, b) => {
        if (a.done === b.done) {
          return 0;
        }
        if (a.done) {
          return 1;
        }
        return -1;
      });
    }
    return filteredNotifications;
  }, [notifications, serviceProviders, clients, isSortedByUnread]);

  const groupedNotifications = isGrouped
    ? validNotifications.reduce((acc, cur) => {
        if (!acc[cur.data.entityName]) {
          // eslint-disable-next-line no-param-reassign
          acc[cur.data.entityName] = [];
        }
        acc[cur.data.entityName].push(cur);
        return acc;
      }, {})
    : {};

  const unreadNotifications = React.useMemo(() => {
    const filteredNotifications = validNotifications.filter((notification) => !notification.done);
    return {
      count:
        filteredNotifications.length +
        sentClientLinkRequests.length +
        receivedClientLinkRequests.length,
      ids: filteredNotifications.map((item) => item.id)
    };
  }, [sentClientLinkRequests.length, receivedClientLinkRequests.length, validNotifications]);

  // use effects
  React.useEffect(() => {
    if (!serviceProviders?.length && !!resourceCentreId)
      loadServiceProviders(Number(resourceCentreId));

    getRemindersAction();

    const interval = setInterval(() => {
      getRemindersAction();
    }, 1000 * 60 * 5);
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    const loadMissingClients = async (): Promise<void> => {
      let missingClientIds = notifications
        .map((notification) => notification.data.clientId)
        .filter(Boolean)
        .filter((clientId) => !clients.some(({ id }) => id === clientId));
      missingClientIds = uniq(missingClientIds);

      if (missingClientIds.length === 0) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const missingClients = (await Promise.all(
          missingClientIds.map((clientId) => getClientDetails(clientId))
        )) as Array<Client>;
        setClients((prevClients) => [...prevClients, ...missingClients]);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };

    if (notificationsRef?.current?.length) {
      loadMissingClients();
    } else {
      setIsLoading(false);
    }
  }, [clients, notifications]);

  // fetch client link requests
  React.useEffect(() => {
    const fetchData = async () => {
      if (rc?.id) {
        const { data } = await getClientLinkRequests({
          rcId: rc.id
        });
        setSentClientLinkRequests(data.sentClientLinkRequests);
        setReceivedClientLinkRequests(data.receivedClientLinkRequests);
      }
    };

    // Fetch immediately
    fetchData();

    // Fetch every 10 minutes
    const interval = setInterval(fetchData, 1000 * 60 * 10);
    return () => clearInterval(interval);
  }, [rc?.id]);

  return (
    <div>
      {!isReadOnlyUser(permissionGroup) && (
        <Tooltip arrow title="Notifications" aria-label="notifications">
          <IconButton
            className={styles.topButtonDropdown}
            onClick={handleClickNotification}
            size="large"
            color="primary"
          >
            <StyledBadge
              invisible={!unreadNotifications.count || isLoading}
              badgeContent={unreadNotifications.count}
              max={20}
            >
              {unreadNotifications.count ? (
                <NotificationsActiveIcon style={{ color: iconColor }} />
              ) : (
                <NotificationsNoneIcon style={{ color: iconColor }} />
              )}
            </StyledBadge>
          </IconButton>
        </Tooltip>
      )}
      <Popper open={Boolean(openNotification)} anchorEl={openNotification} placement="bottom-end">
        <ClickAwayListener onClickAway={handleCloseNotification}>
          <Paper elevation={2} className={styles.notificationBox}>
            <Box px={2} py={1} display="flex" alignItems="center" gap={2}>
              <Typography fontSize={14} fontWeight={600} component="span" flex={1}>
                Notifications
              </Typography>
              <FormControlLabel
                label="Group by modules"
                control={
                  <Checkbox
                    size="small"
                    checked={isGrouped}
                    onChange={() => setIsGrouped(!isGrouped)}
                  />
                }
              />
              <Button
                disabled={!unreadNotifications.count}
                size="small"
                sx={{ textTransform: "capitalize" }}
                onClick={() => dispatch(markAllReminderRead(unreadNotifications.ids))}
              >
                Mark all as read
              </Button>
              <Button
                title="Sort by unread"
                color={isSortedByUnread ? "primary" : "inherit"}
                onClick={() => setIsSortedByUnread(!isSortedByUnread)}
                sx={{ textTransform: "capitalize" }}
              >
                {`Unread (${unreadNotifications.count})`}
              </Button>
            </Box>

            <Divider />

            <MenuList role="menu" className={styles.notificationItems}>
              {!isGrouped
                ? validNotifications.map((notification) => {
                    const serviceProvider = serviceProviders.find(
                      (provider) => provider.id === notification.data.serviceProviderId
                    );
                    const client = clients?.find(
                      (clientI) => clientI.id === Number(notification.data.clientId)
                    );
                    return (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        closeNotification={handleCloseNotification}
                        client={client}
                        serviceProvider={serviceProvider}
                      />
                    );
                  })
                : Object.keys(groupedNotifications).map((name) => {
                    const group = groupedNotifications[name];
                    return (
                      <div key={name}>
                        <Typography className={styles.notificationGroupTitle}>
                          {startCase(name)}
                        </Typography>
                        {group.map((item) => {
                          const serviceProvider = serviceProviders.find(
                            (provider) => provider.id === item.data.serviceProviderId
                          );
                          const client = clients?.find(
                            (clientI) => clientI.id === Number(item.data.clientId)
                          );
                          return (
                            <NotificationItem
                              hideBadge
                              key={item.id}
                              notification={item}
                              closeNotification={handleCloseNotification}
                              client={client}
                              serviceProvider={serviceProvider}
                            />
                          );
                        })}
                      </div>
                    );
                  })}

              {validNotifications.length === 0 && (
                <MenuItem className={styles.noHover} sx={{ py: 4 }}>
                  No new notifications
                </MenuItem>
              )}
            </MenuList>

            <Divider />

            <ClientLinkRequests
              sentClientLinkRequests={sentClientLinkRequests}
              setSentClientLinkRequests={setSentClientLinkRequests}
              receivedClientLinkRequests={receivedClientLinkRequests}
              setReceivedClientLinkRequests={setReceivedClientLinkRequests}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};

const mapStateToProps = ({ userContext, resources, reminders }) => {
  const filteredReminders =
    userContext.mode === "serviceProvider"
      ? reminders.filter((reminder) => reminder.data.serviceProviderId === userContext.user.id)
      : reminders;

  return {
    resourceCentreId: userContext.resourceCentreId,
    notifications: filteredReminders,
    serviceProviders: resources.resourceCentreServiceProviders,
    permissionGroup:
      userContext && userContext.userCreds.userGroups && userContext.userCreds.userGroups[0]
  };
};

const matchDispatchToProps = (dispatch) => ({
  getRemindersAction: () => {
    dispatch(getReminders());
  },
  loadServiceProviders: (id) =>
    dispatch(serviceProviderActions.getResourceCentreServiceProviders({ resourceCentreId: id }))
});

export default connect(mapStateToProps, matchDispatchToProps)(Notifications);
